import React, { useState, useEffect } from 'react'
import { Trophy, TrendingUp, User, DollarSign, Calendar, Search, CheckCircle, ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { BaseDetailView } from './BaseDetailView'
import { TimeframeSelector } from './TimeframeSelector'

interface DealsClosedDetailViewProps {
  onBack: () => void
  clientFilter?: string
  timeFrame?: string
  customDateRange?: {
    from: Date
    to: Date
  }
  onTimeFrameChange?: (timeFrame: string) => void
  onCustomDateRangeChange?: (dateRange: { from: Date; to: Date } | undefined) => void
}

interface DealsMetrics {
  totalDeals: number
  totalRevenue: number
  closeRate: number
  averageDealSize: number
}

interface MeetingsData {
  meetings_booked: number
  breakcold_meetings: number
  attribution_meetings: number
  overlap_count: number
}

interface QualifiedData {
  breakcold_qualified: number
  sals_qualified: number
  total_qualified: number
}

export const DealsClosedDetailView = ({ 
  onBack, 
  clientFilter = 'all', 
  timeFrame = 'MTD', 
  customDateRange,
  onTimeFrameChange,
  onCustomDateRangeChange
}: DealsClosedDetailViewProps) => {
  const [dealsMetrics, setDealsMetrics] = useState<DealsMetrics>({
    totalDeals: 0,
    totalRevenue: 0,
    closeRate: 0,
    averageDealSize: 0
  })
  const [isLoading, setIsLoading] = useState(true)

  // Calculate date range based on timeFrame and customDateRange
  const getDateRange = () => {
    if (timeFrame === 'CUSTOM' && customDateRange?.from && customDateRange?.to) {
      return {
        startDate: customDateRange.from.toISOString(),
        endDate: customDateRange.to.toISOString()
      }
    }
    
    // Default to current month for MTD or fallback
    const now = new Date()
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString()
    return { startDate, endDate }
  }

  const { startDate, endDate } = getDateRange()

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k'
    return num.toString()
  }

  const convertAmountToUSD = (amount: string): number => {
    if (!amount) return 0
    
    // Remove currency symbols and clean the string, keeping only numbers, dots, commas, and minus signs
    const cleanAmount = amount.replace(/[^0-9.,\-]/g, '')
    
    // Remove commas (thousands separators)
    const withoutCommas = cleanAmount.replace(/,/g, '')
    
    const numericValue = parseFloat(withoutCommas)
    
    if (isNaN(numericValue)) return 0
    
    // For now, assume all amounts are already in USD equivalent
    // In the future, you might want to add actual currency conversion
    return numericValue
  }

  const normalizeDomain = (domain: string): string => {
    if (!domain) return ''
    
    // Convert to lowercase and trim
    let normalized = domain.toLowerCase().trim()
    
    // Remove protocol if present
    normalized = normalized.replace(/^https?:\/\//, '')
    
    // Remove www. prefix
    normalized = normalized.replace(/^www\./, '')
    
    // Remove trailing slash
    normalized = normalized.replace(/\/$/, '')
    
    // Extract base domain (remove subdomains for better matching)
    // Keep only the last two parts (domain.tld)
    const parts = normalized.split('.')
    if (parts.length > 2) {
      // Keep last two parts for base domain
      normalized = parts.slice(-2).join('.')
    }
    
    return normalized
  }

  const fetchMeetingsAndQualifiedDomains = async () => {
    try {
      // Use the same RPC functions that power the dashboard metrics
       const { data: meetingsData } = await supabase.rpc('get_conversion_funnel_meetings_booked_analytics', {
         start_date: startDate,
         end_date: endDate,
         target_client_id: clientFilter
       })

       const { data: qualifiedData } = await supabase.rpc('get_conversion_funnel_qualified_meetings_analytics', {
         start_date: startDate,
         end_date: endDate,
         target_client_id: clientFilter
       })

      // Get the actual detailed records that contribute to these numbers
      const allDomains = new Set<string>()

      // Get domains from the new meetings_booked table that are counted in the dashboard
      const { data: allMeetings } = await supabase
        .from('meetings_booked')
        .select('email_domain, client_id, source, meetings_booked_create_date, lead_email')
        .gte('meetings_booked_create_date', startDate)
        .lte('meetings_booked_create_date', endDate)

      // Qualified meetings - only get qualified leads that are counted
      const { data: breakcoldQualified } = await supabase
        .from('lead_category_updates')
        .select('lead_email, client_id, created_at')
        .eq('category', 'Qualified Meeting')
        .gte('created_at', startDate)
        .lte('created_at', endDate)

      // Store domain sources for attribution tracking
      const domainSources = new Map<string, { source: string, details: string }>()

      // Add domains from meetings_booked table
      allMeetings?.forEach(meeting => {
        if (meeting.email_domain) {
          const normalized = normalizeDomain(meeting.email_domain)
          if (normalized) {
            allDomains.add(normalized)
            domainSources.set(normalized, {
              source: 'Meetings Booked',
              details: meeting.source === 'sqls_meetings_booked' ? 'Attribution' : 'Direct'
            })
          }
        }
      })

      // Add domains from qualified leads
      breakcoldQualified?.forEach(qualified => {
        if (qualified.lead_email && qualified.lead_email.includes('@')) {
          const domain = qualified.lead_email.split('@')[1]
          const normalized = normalizeDomain(domain)
          if (normalized) {
            allDomains.add(normalized)
            domainSources.set(normalized, {
              source: 'Qualified Meetings',
              details: `Breakcold Qualified`
            })
          }
        }
      })

      return { domains: Array.from(allDomains), domainSources }
    } catch (error) {
      console.error('Error fetching domains:', error)
      return { domains: [], domainSources: new Map() }
    }
  }

  const fetchDealsMetrics = async () => {
    try {
      setIsLoading(true)
      
      // Get domains from actual dashboard metrics
      const { domains } = await fetchMeetingsAndQualifiedDomains()
      
      if (domains.length === 0) {
        setDealsMetrics({
          totalDeals: 0,
          totalRevenue: 0,
          closeRate: 0,
          averageDealSize: 0
        })
        return
      }

      // Get all deals and filter by normalized domains
      const { data: allDeals, error } = await supabase
        .from('deals_won')
        .select('*')
        .gte('created_at', startDate)
        .lte('created_at', endDate)

      if (error) throw error

      // Filter deals by matching normalized domains
      const deals = allDeals?.filter(deal => {
        if (!deal.opportunity_domain__c) return false
        const normalizedDealDomain = normalizeDomain(deal.opportunity_domain__c)
        return domains.includes(normalizedDealDomain)
      }) || []

      // Calculate metrics
      const totalDeals = deals?.length || 0
      const totalRevenue = deals?.reduce((sum, deal) => {
        return sum + convertAmountToUSD(deal.amount || '0')
      }, 0) || 0

      // Use the actual qualified meetings count from dashboard RPC
      const { data: qualifiedData } = await supabase.rpc('get_conversion_funnel_qualified_meetings_analytics', {
        start_date: startDate,
        end_date: endDate,
        target_client_id: 'all'
      })

      const totalQualified = (qualifiedData as unknown as QualifiedData)?.total_qualified || 0
      const closeRate = totalQualified > 0 ? (totalDeals / totalQualified) * 100 : 0
      const averageDealSize = totalDeals > 0 ? totalRevenue / totalDeals : 0

      setDealsMetrics({
        totalDeals,
        totalRevenue,
        closeRate,
        averageDealSize
      })
    } catch (error) {
      console.error('Error fetching deals metrics:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchDealsMetrics()
  }, [])

  const fetchDetailRecords = async (startDate: string, endDate: string) => {
    try {
      // Get domains and their sources from actual dashboard metrics
      const { domains, domainSources } = await fetchMeetingsAndQualifiedDomains()
      
      if (domains.length === 0) return []

      const { data: allData, error } = await supabase
        .from('deals_won')
        .select(`
          account_id,
          account_name,
          full_name,
          opportunity_name,
          amount,
          amount_convert,
          stage_name,
          close_date,
          created_at,
          client,
          lead_source,
          probability,
          opportunity_package__c,
          opportunity_domain__c
        `)
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: false })
      
      if (error) throw error

      // Filter deals by matching normalized domains and add attribution info
      const filteredData = allData?.filter(deal => {
        if (!deal.opportunity_domain__c) return false
        const normalizedDealDomain = normalizeDomain(deal.opportunity_domain__c)
        return domains.includes(normalizedDealDomain)
      }).map(deal => {
        const normalizedDealDomain = normalizeDomain(deal.opportunity_domain__c)
        const attribution = domainSources.get(normalizedDealDomain)
        return {
          ...deal,
          source_type: attribution?.source || 'Unknown',
          source_details: attribution?.details || 'No attribution found',
          attribution_confidence: attribution ? 'High' : 'Low'
        }
      }) || []

      return filteredData.slice(0, 100)
    } catch (error) {
      console.error('Error fetching detail records:', error)
      return []
    }
  }

  const config = {
    title: "Deals Closed Details",
    description: "Complete breakdown of closed deals attributed to outbound efforts",
    metricsCards: [
      {
        title: "Deals Closed (MTD)",
        value: isLoading ? "..." : formatNumber(dealsMetrics.totalDeals),
        description: `${dealsMetrics.closeRate.toFixed(1)}% close rate from qualified leads`,
        icon: <Trophy className="h-4 w-4 text-muted-foreground" />
      },
      {
        title: "Close Rate",
        value: isLoading ? "..." : `${dealsMetrics.closeRate.toFixed(1)}%`,
        description: "Of all qualified meetings",
        icon: <TrendingUp className="h-4 w-4 text-muted-foreground" />
      },
      {
        title: "Total Revenue",
        value: isLoading ? "..." : `$${formatNumber(dealsMetrics.totalRevenue)}`,
        description: "Revenue from attributed deals",
        icon: <DollarSign className="h-4 w-4 text-muted-foreground" />
      },
      {
        title: "Average Deal Size",
        value: isLoading ? "..." : `$${formatNumber(dealsMetrics.averageDealSize)}`,
        description: "Per closed deal",
        icon: <DollarSign className="h-4 w-4 text-muted-foreground" />
      }
    ],
    chartTitle: "Daily Deals Closed",
    chartDescription: "Deals closed per day this month - Click on bars to filter",
    detailsTable: {
      title: "Detailed Closed Deal Records",
      description: "Individual closed deal records with revenue details",
      columns: [
        { header: "Timestamp", key: "created_at", render: (value: string) => 
          <div className="font-mono text-sm">{value ? new Date(value).toLocaleString() : 'N/A'}</div>
        },
        { header: "Account", key: "account_name", render: (value: string) => 
          <div className="font-medium">{value || 'Unknown Account'}</div>
        },
        { header: "Contact", key: "full_name", render: (value: string) => 
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            {value || 'Unknown'}
          </div>
        },
        { header: "Opportunity", key: "opportunity_name", render: (value: string) => 
          <div className="max-w-[200px] truncate" title={value || 'N/A'}>
            {value || 'N/A'}
          </div>
        },
        { header: "Amount", key: "amount", render: (value: string) => 
          <div className="flex items-center gap-1 font-semibold text-green-600">
            <DollarSign className="h-4 w-4" />
            {value ? `$${value}` : 'N/A'}
          </div>
        },
        { header: "Domain", key: "opportunity_domain__c", render: (value: string) => 
          <div className="font-mono text-sm text-blue-600">{value || 'N/A'}</div>
        },
        { header: "Source Type", key: "source_type", render: (value: string) => 
          <Badge variant={value === 'Meetings Booked' ? 'default' : 'secondary'} className="text-xs">
            {value}
          </Badge>
        },
        { header: "Source Details", key: "source_details", render: (value: string) => 
          <div className="text-sm text-muted-foreground max-w-[150px] truncate" title={value}>
            {value}
          </div>
        },
        { header: "Attribution", key: "attribution_confidence", render: (value: string) => 
          <div className="flex items-center gap-1">
            {value === 'High' ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <Search className="h-4 w-4 text-orange-500" />
            )}
            <span className={`text-xs ${value === 'High' ? 'text-green-600' : 'text-orange-600'}`}>
              {value}
            </span>
          </div>
        },
        { header: "Package", key: "opportunity_package__c", render: (value: string) => 
          <div className="text-sm">{value || 'N/A'}</div>
        },
        { header: "Close Date", key: "close_date", render: (value: string) => 
          <div className="flex items-center gap-1 font-mono text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            {value ? new Date(value).toLocaleDateString() : 'N/A'}
          </div>
        }
      ]
    },
    csvFileName: "deals-closed",
    fetchDetailRecords,
    realtimeChannelName: "deals-closed-updates",
    realtimeTables: ["deals_won"]
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Timeframe Selector */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        {onTimeFrameChange && onCustomDateRangeChange && (
          <TimeframeSelector
            timeFrame={timeFrame}
            customDateRange={customDateRange}
            onTimeFrameChange={onTimeFrameChange}
            onCustomDateRangeChange={onCustomDateRangeChange}
          />
        )}
      </div>

      <BaseDetailView 
        config={config} 
        onBack={onBack} 
      />
    </div>
  )
}